import { Link } from "@tanstack/react-router";
/**
 * 候选召回（`#recall`，应答配置 › 候选召回）——P1 召回第 5 步新建（判定卡 11、线框 05；方案 v1.0 §六），后台对齐批 4 补全
 * （摸底 C_应答配置 第 7 条、D_规则字段配置 第 14 条）。
 * 标签体系 v2 批 3（画像 pv4 起）：工具召回改「相关性为主、意图加分」——意图改为意图标签（只加分）、说法并进「何时用」例句、
 * 删适用数据状态；本页按画像的召回口径（recall_mode）显示，旧画像照旧显示四路字段。
 * 标签体系 v2 批 4（画像 pv5 起，guide_mode=relevance）：指南召回改相关性判断——按「适用条件」文字逐条判，意图标签与情绪风险标签
 * 只加分；挂到哪些工具 ／ 实体条件 ／ 对话条件 ／ 语义条件退场（并进适用条件文字）；数据状态与信息项条件是系统事实。
 * 看和改召回用到的画像（工具画像、指南召回条件）；改动先进草稿，「保存为新版本」时自动跑回归（开发集 Jev 臂，与运行时同一条路，
 * 约 50～100 次 Jev 调用、要等十几秒到半分钟；草稿那遍只问改到的题；收尾 B6，之前跑粗召回、pv4 下恒满分拦不住改坏），
 * 工具召回、第 1 名命中、推荐档命中、指南召回不降、指南误召回不升才放行；保存即写进新配置快照（收尾 B1）。枚举表（实体类型、数据状态、对话状态）只读——维护边界第 ① 层只有我们改。
 * 其余页签只读（`GET /recall/schema`，见 recall-structure.tsx）：召回规则、数据状态（怎么算、谁在用）、排序与呈现（按生效引擎显示）、
 * 指南召回的五种来源、知识推送与约束；召回评测在规则签底部给入口。
 */
import { useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Drawer } from "@/components/ui/drawer";
import { LoadingInline } from "@/components/ui/feedback";
import { Input, Textarea } from "@/components/ui/input";
import { LabeledSelect, Select } from "@/components/ui/select";
import { Tabs } from "@/components/ui/tabs";
import {
	type RecallGuide,
	type RecallProfile,
	type RecallTool,
	planErrorMessage,
	useRecallProfile,
	useRecallProfiles,
	useRecallRegression,
	useRecallSaveProfile,
	useRecallSchema,
} from "@/lib/api/plan-lab";
import { cn } from "@/lib/utils";

import { byFacts, guideTags, signalItems } from "../guidance/guidance-tab";
import { FailState } from "../kit/fail-state";
import { PageJump } from "../kit/page-jump";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";
import {
	ENTITY_LABEL,
	METRIC_LABEL,
	STATE_LABEL,
	fmtRate,
	labelOf,
	slotCondText,
} from "./recall-labels";
import {
	DataStatesPanel,
	GuideSourcesCard,
	KnowledgePanel,
	RankPanel,
	RecallEvalCard,
} from "./recall-structure";

type TabKey = "tools" | "guides" | "rules" | "states" | "rank" | "knowledge";
const TABS: { key: TabKey; label: string }[] = [
	{ key: "tools", label: "工具画像" },
	{ key: "guides", label: "指南召回" },
	{ key: "rules", label: "召回规则" },
	{ key: "states", label: "数据状态" },
	{ key: "rank", label: "排序与呈现" },
	{ key: "knowledge", label: "知识推送" },
];
const ALL = "__all__";
type MountKey = "relevance" | "standing" | "facts";
const mountOf = (g: RecallGuide): Exclude<MountKey, "facts"> =>
	g.standing ? "standing" : "relevance";
const RISK_LABEL: Record<string, string> = { read: "读", write: "写", exit: "出口" };

type Draft = { tools: RecallTool[]; guides: RecallGuide[] };

/** 画像版本说明一行：来源 ＋ 备注第一句（全文放悬浮）；备注自己已写「随代码发的种子」就不再重复前缀 */
function versionMetaLine(
	meta: { saved_at?: string | null; operator?: string | null; note?: string | null } | undefined,
): string {
	const first = (meta?.note ?? "").split(/[。\n]/)[0]?.trim() ?? "";
	const head = meta?.saved_at
		? `保存于 ${meta.saved_at.slice(0, 16).replace("T", " ")} · ${meta.operator ?? ""}`
		: first.startsWith("随代码发的种子")
			? ""
			: "随代码发的种子";
	return [head, first].filter(Boolean).join(" · ");
}

export function RecallTab() {
	const versions = useRecallProfiles();
	const [version, setVersion] = useState<string>();
	useEffect(() => {
		if (!version && versions.data) setVersion(versions.data.active);
	}, [version, versions.data]);
	const prof = useRecallProfile(version);
	const [tab, setTab] = useState<TabKey>("tools");
	const [draft, setDraft] = useState<Draft | null>(null);
	const [editTool, setEditTool] = useState<RecallTool | null>(null);
	const [editGuide, setEditGuide] = useState<RecallGuide | null>(null);
	const [saving, setSaving] = useState(false);

	// 换版本时丢掉草稿（草稿只对它的底版本有效）
	// biome-ignore lint/correctness/useExhaustiveDependencies: version 变了就清草稿，依赖是有意的
	useEffect(() => setDraft(null), [version]);

	const view: Draft | null =
		draft ?? (prof.data ? { tools: prof.data.tools, guides: prof.data.guides } : null);
	const changed = useMemo(() => changedIds(prof.data, draft), [prof.data, draft]);

	if (versions.isLoading || (version && prof.isLoading))
		return (
			<Card data-testid="plan-block-recall-version">
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	const err = versions.error ?? prof.error;
	if (versions.isError || prof.isError || !prof.data || !view)
		return (
			<Card data-testid="plan-block-recall-version">
				<CardContent>
					<FailState
						reason={err ? planErrorMessage(err) : "没有读到画像"}
						onRetry={() => {
							versions.refetch();
							prof.refetch();
						}}
						retrying={versions.isFetching || prof.isFetching}
						testId="plan-recall-fail"
					/>
				</CardContent>
			</Card>
		);

	const meta = versions.data?.versions.find((v) => v.version === version);
	const nChanged = changed.tools.size + changed.guides.size;

	return (
		<div className="space-y-4" data-testid="plan-recall">
			<Card data-testid="plan-block-recall-version">
				<CardContent className="flex flex-wrap items-center gap-3 py-3 text-sm">
					<span className="text-text-secondary">画像版本</span>
					<Select value={version} onChange={(e) => setVersion(e.target.value)} className="w-56">
						{versions.data?.versions.map((v) => (
							<option key={v.version} value={v.version}>
								{v.version}
								{v.active ? "（当前生效）" : ""}
							</option>
						))}
					</Select>
					<span
						className="max-w-[48ch] truncate text-xs text-text-muted"
						title={meta?.note || undefined}
						data-testid="plan-recall-version-meta"
					>
						{versionMetaLine(meta)}
					</span>
					{prof.data.recall_mode !== "relevance" ? (
						<span
							className="basis-full text-xs text-text-muted"
							data-testid="plan-recall-legacy-note"
						>
							这是 pv4
							及更早的画像：运行时按当时的口径（意图路、实体路等四路）召回，本页只按当前口径（相关性判断、意图标签只加分）展示，
							意图列读旧键 intents；要复现旧口径看这个版本的快照。
						</span>
					) : null}
					<span className="ml-auto flex items-center gap-3">
						{nChanged ? (
							<span className="text-xs text-text-secondary">草稿改了 {nChanged} 处</span>
						) : null}
						{nChanged ? (
							<Button type="button" size="sm" variant="ghost" onClick={() => setDraft(null)}>
								放弃草稿
							</Button>
						) : null}
						<Button
							type="button"
							size="sm"
							disabled={!nChanged}
							title={nChanged ? undefined : "还没有改动：先在下面点一行编辑"}
							onClick={() => setSaving(true)}
							data-testid="plan-recall-save"
						>
							保存为新版本
						</Button>
					</span>
				</CardContent>
			</Card>

			<div className="space-y-3" data-testid="plan-block-recall-body">
				<Tabs items={TABS} active={tab} onChange={setTab} />
				{tab === "tools" ? (
					<ToolsTable
						tools={view.tools}
						intents={prof.data.intents ?? []}
						changed={changed.tools}
						onOpen={setEditTool}
					/>
				) : null}
				{tab === "guides" ? (
					<div className="space-y-3">
						<GuideSourcesCard />
						<GuidesTable
							guides={view.guides}
							signalNames={prof.data.signal_tags}
							intents={prof.data.intents ?? []}
							changed={changed.guides}
							onOpen={setEditGuide}
						/>
					</div>
				) : null}
				{tab === "rules" ? <RulesPanel prof={prof.data} /> : null}
				{tab === "states" ? <DataStatesPanel /> : null}
				{tab === "rank" ? <RankPanel /> : null}
				{tab === "knowledge" ? <KnowledgePanel /> : null}
			</div>

			<ToolDrawer
				tool={editTool}
				prof={prof.data}
				onClose={() => setEditTool(null)}
				onApply={(t) => {
					setDraft({ ...view, tools: view.tools.map((x) => (x.tool_id === t.tool_id ? t : x)) });
					setEditTool(null);
				}}
			/>
			<GuideDrawer
				guide={editGuide}
				prof={prof.data}
				onClose={() => setEditGuide(null)}
				onApply={(g) => {
					setDraft({ ...view, guides: view.guides.map((x) => (x.id === g.id ? g : x)) });
					setEditGuide(null);
				}}
			/>
			{saving && version && draft ? (
				<SaveDialog
					base={version}
					draft={draft}
					nChanged={nChanged}
					onClose={() => setSaving(false)}
					onSaved={(v) => {
						setSaving(false);
						setDraft(null);
						setVersion(v);
					}}
				/>
			) : null}
		</div>
	);
}

function changedIds(prof: RecallProfile | undefined, draft: Draft | null) {
	const tools = new Set<string>();
	const guides = new Set<string>();
	if (!prof || !draft) return { tools, guides };
	const t0 = new Map(prof.tools.map((t) => [t.tool_id, JSON.stringify(t)]));
	const g0 = new Map(prof.guides.map((g) => [g.id, JSON.stringify(g)]));
	for (const t of draft.tools) if (t0.get(t.tool_id) !== JSON.stringify(t)) tools.add(t.tool_id);
	for (const g of draft.guides) if (g0.get(g.id) !== JSON.stringify(g)) guides.add(g.id);
	return { tools, guides };
}

function Dot({ on }: { on: boolean }) {
	return on ? (
		<span className="inline-block h-2 w-2 rounded-full bg-brand-accent" title="草稿里改过" />
	) : null;
}

/** 工具挂的意图：pv4 起键 intent_tags（只加分），旧画像 intents（召回路） */
const toolIntents = (t: RecallTool): string[] => t.intent_tags ?? t.intents ?? [];

function ToolsTable({
	tools,
	intents,
	changed,
	onOpen,
}: {
	tools: RecallTool[];
	intents: { key: string; name: string }[];
	changed: Set<string>;
	onOpen: (t: RecallTool) => void;
}) {
	const intentName = (k: string) => intents.find((i) => i.key === k)?.name ?? k;
	const cols: PlanColumn<RecallTool>[] = [
		{
			key: "id",
			title: "工具",
			className: "w-40",
			render: (t) => <span className="font-mono">{t.tool_id}</span>,
		},
		{
			key: "risk",
			title: "风险",
			className: "w-14",
			render: (t) => RISK_LABEL[t.risk ?? ""] ?? t.risk ?? "—",
		},
		{
			key: "standing",
			title: "常备",
			className: "w-14",
			render: (t) => (t.standing ? "常备" : ""),
		},
		{
			key: "intents",
			title: "意图标签（只加分）",
			render: (t) => <CellTags items={toolIntents(t).map((i) => intentName(i))} />,
		},
		{
			key: "ents",
			title: "业务对象（组参）",
			render: (t) => <CellTags items={(t.entities ?? []).map((e) => labelOf(ENTITY_LABEL, e))} />,
		},
		{
			key: "slots",
			title: "信息项",
			render: (t) => <CellTags mono items={t.slots ?? []} />,
		},
		{
			key: "pre",
			title: "执行前置（确定不能用）",
			render: (t) => (
				<CellTags
					tone="warning"
					items={(t.preconditions ?? []).map((p) => labelOf(STATE_LABEL, p.state))}
				/>
			),
		},
		{
			key: "chg",
			title: "改过",
			className: "w-12",
			render: (t) => <Dot on={changed.has(t.tool_id)} />,
		},
	];
	return (
		<div className="space-y-2">
			<PlanTable
				columns={cols}
				rows={tools}
				rowKey={(t) => t.tool_id}
				onRowClick={onOpen}
				rowTestId="plan-recall-tool-row"
			/>
			<p className="text-xs text-text-muted">
				共 {tools.length} 条 · 点一行编辑 · 工具契约见 <PageJump to="tools" />
				，意图说明见 <PageJump to="tags" />
			</p>
		</div>
	);
}

function GuidesTable({
	guides,
	signalNames,
	intents,
	changed,
	onOpen,
}: {
	guides: RecallGuide[];
	signalNames?: Record<"sentiment" | "risk_flags", { key: string; name: string }[]>;
	intents: { key: string; name: string }[];
	changed: Set<string>;
	onOpen: (g: RecallGuide) => void;
}) {
	const [mount, setMount] = useState<string>(ALL);
	const intentName = (k: string) => intents.find((i) => i.key === k)?.name ?? k;
	const rows = guides.filter(
		(g) => mount === ALL || (mount === "facts" ? byFacts(g) : mountOf(g) === mount),
	);
	const n = (k: MountKey) =>
		guides.filter((g) => (k === "facts" ? byFacts(g) : mountOf(g) === k)).length;
	const names = signalNamesOf(signalNames);
	const cols: PlanColumn<RecallGuide>[] = [
		{
			key: "id",
			title: "指南",
			className: "w-56",
			render: (g) => <span className="font-mono">{g.id}</span>,
		},
		{
			key: "cond",
			title: "适用条件（按它判相关）",
			className: "max-w-[260px]",
			render: (g) => (
				<span className="block truncate text-text-secondary" title={g.condition_text}>
					{g.condition_text || "—"}
				</span>
			),
		},
		{
			key: "tags",
			title: "标签（只加分）",
			render: (g) =>
				g.standing ? (
					<Badge tone="info">常备</Badge>
				) : (
					<CellTags items={[...guideTags(g).map(intentName), ...signalItems(g, names)]} />
				),
		},
		{
			key: "facts",
			title: "系统事实条件",
			render: (g) => (
				<CellTags
					items={[
						...(g.data_conds ?? []).map((s) => labelOf(STATE_LABEL, s)),
						...(g.slot_conds ?? []).map(slotCondText),
					]}
				/>
			),
		},
		{
			key: "mode",
			title: "执行方式",
			className: "w-20",
			render: (g) => (g.mode === "once" ? "一次性" : "持续"),
		},
		{ key: "chg", title: "改过", className: "w-12", render: (g) => <Dot on={changed.has(g.id)} /> },
	];
	return (
		<div className="space-y-2">
			<div className="flex flex-wrap items-center gap-2">
				<LabeledSelect
					label="挂载"
					value={mount}
					onChange={setMount}
					options={[
						{ value: ALL, label: `全部（${guides.length}）` },
						{ value: "relevance", label: `相关性判断（${n("relevance")}）` },
						{ value: "standing", label: `常备（${n("standing")}）` },
						{ value: "facts", label: `带按事实补（${n("facts")}）` },
					]}
				/>
			</div>
			<PlanTable
				columns={cols}
				rows={rows}
				rowKey={(g) => g.id}
				onRowClick={onOpen}
				rowTestId="plan-recall-guide-row"
			/>
			<p className="text-xs text-text-muted">
				当前 {rows.length} 条 · 点一行编辑召回条件（正文、强度、注入预览在
				<Link
					to="/plan-lab"
					hash="guidance"
					className="text-brand-accent hover:underline"
					data-testid="plan-recall-guidance-link"
				>
					「应答指南」页
				</Link>
				看）
			</p>
		</div>
	);
}

/** 多选标签：取值只能来自枚举（维护边界 ①），选中橙 accent（component-standards §二） */
function ChipPicker({
	options,
	value,
	onChange,
	label,
	testId,
}: {
	options: string[];
	value: string[];
	onChange: (v: string[]) => void;
	label?: (v: string) => string;
	testId?: string;
}) {
	return (
		<div className="flex flex-wrap gap-1.5" data-testid={testId}>
			{options.map((o) => {
				const on = value.includes(o);
				return (
					<button
						key={o}
						type="button"
						aria-pressed={on}
						onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
						className={cn(
							"rounded-full border px-2.5 py-0.5 text-xs transition-colors duration-150",
							on
								? "border-brand-accent bg-brand-accent/5 text-text-primary"
								: "border-surface-border text-text-secondary hover:text-text-primary",
						)}
					>
						{label ? label(o) : o}
					</button>
				);
			})}
		</div>
	);
}

function Field({
	label,
	children,
	hint,
}: { label: string; children: React.ReactNode; hint?: string }) {
	return (
		<div className="space-y-1.5">
			<div className="text-xs font-medium text-text-secondary">{label}</div>
			{children}
			{hint ? <div className="text-xs text-text-muted">{hint}</div> : null}
		</div>
	);
}

function ToolDrawer({
	tool,
	prof,
	onClose,
	onApply,
}: {
	tool: RecallTool | null;
	prof: RecallProfile;
	onClose: () => void;
	onApply: (t: RecallTool) => void;
}) {
	const [t, setT] = useState<RecallTool | null>(tool);
	useEffect(() => setT(tool), [tool]);
	if (!t) return null;
	const set = (patch: Partial<RecallTool>) => setT({ ...t, ...patch });
	const desc = t.desc ?? {};
	const toolIds = prof.tools.map((x) => x.tool_id).filter((x) => x !== t.tool_id);
	return (
		<Drawer
			open={!!tool}
			onClose={onClose}
			width="min(760px,95vw)"
			testId="plan-recall-tool-drawer"
			title={<span className="font-mono">{t.tool_id}</span>}
			description={`风险：${RISK_LABEL[t.risk ?? ""] ?? t.risk ?? "—"}${t.standing ? " · 常备" : ""}`}
		>
			<div className="space-y-5">
				<Field
					label="意图标签（只加分）"
					hint="诉求意图在这里面时给相关度加分；不在也不剔除——工具能不能进候选看说明写得清不清"
				>
					<ChipPicker
						options={(prof.intents ?? []).map((i) => i.key)}
						value={toolIntents(t)}
						onChange={(v) => set({ intent_tags: v })}
						label={(v) => (prof.intents ?? []).find((i) => i.key === v)?.name ?? v}
					/>
				</Field>
				<Field label="需要／能处理的业务对象" hint="只用于组参、定位、绑订单，不作召回路">
					<ChipPicker
						options={prof.entity_types}
						value={t.entities ?? []}
						onChange={(v) => set({ entities: v })}
						label={(v) => labelOf(ENTITY_LABEL, v)}
					/>
				</Field>
				{prof.slot_keys?.length ? (
					<Field
						label="用到的信息项"
						hint="只用于组参（原因、件数、金额、商品属性、问题、地址批 2 起是信息项），不作召回路"
					>
						<ChipPicker
							options={prof.slot_keys}
							value={t.slots ?? []}
							onChange={(v) => set({ slots: v })}
							label={(v) => v}
						/>
					</Field>
				) : null}
				<Field
					label="执行前置：数据处于这些状态时确定不能用（召回只标注、不删候选）"
					hint="只写确定的事实；拿不准的不要放进准入（会把正确的工具挡掉）"
				>
					<ChipPicker
						options={prof.data_states}
						value={(t.preconditions ?? []).map((p) => p.state)}
						onChange={(v) =>
							set({
								preconditions: v.map(
									(s) =>
										(t.preconditions ?? []).find((p) => p.state === s) ?? { state: s, reason: "" },
								),
							})
						}
						label={(v) => labelOf(STATE_LABEL, v)}
					/>
				</Field>
				<div className="grid gap-5 sm:grid-cols-2">
					<Field label="前置工具">
						<ChipPicker
							options={toolIds}
							value={t.prerequisites ?? []}
							onChange={(v) => set({ prerequisites: v })}
						/>
					</Field>
					<Field label="易混工具">
						<ChipPicker
							options={toolIds}
							value={t.confusable ?? []}
							onChange={(v) => set({ confusable: v })}
						/>
					</Field>
				</div>
				<Field label="做什么">
					<Input
						value={desc.what ?? ""}
						onChange={(e) => set({ desc: { ...desc, what: e.target.value } })}
					/>
				</Field>
				<Field
					label="何时用"
					hint="相关性判断按说明判：末尾写「客户常这么说：「…」「…」」放几条例句；没召回到就改说明，不加对应关系"
				>
					<Textarea
						rows={5}
						value={desc.when ?? ""}
						onChange={(e) => set({ desc: { ...desc, when: e.target.value } })}
					/>
				</Field>
				<Field label="何时不用（写明排除条件并指向替代工具）">
					<Textarea
						rows={3}
						value={desc.when_not ?? ""}
						onChange={(e) => set({ desc: { ...desc, when_not: e.target.value } })}
					/>
				</Field>
				<Field label="需要什么、提供什么">
					<Textarea
						rows={2}
						value={desc.needs_provides ?? ""}
						onChange={(e) => set({ desc: { ...desc, needs_provides: e.target.value } })}
					/>
				</Field>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" size="sm" onClick={onClose}>
						取消
					</Button>
					<Button type="button" size="sm" onClick={() => onApply(t)}>
						应用到草稿
					</Button>
				</div>
			</div>
		</Drawer>
	);
}

function GuideDrawer({
	guide,
	prof,
	onClose,
	onApply,
}: {
	guide: RecallGuide | null;
	prof: RecallProfile;
	onClose: () => void;
	onApply: (g: RecallGuide) => void;
}) {
	const [g, setG] = useState<RecallGuide | null>(guide);
	useEffect(() => setG(guide), [guide]);
	if (!g) return null;
	const set = (patch: Partial<RecallGuide>) => setG({ ...g, ...patch });
	return (
		<Drawer
			open={!!guide}
			onClose={onClose}
			width="min(760px,95vw)"
			testId="plan-recall-guide-drawer"
			title={<span className="font-mono">{g.id}</span>}
			description={g.title}
		>
			<div className="space-y-5">
				<div className="space-y-1 rounded-md border border-surface-border bg-surface-muted/40 p-3 text-xs text-text-secondary">
					<div>
						<span className="text-text-muted">条件原文：</span>
						{g.condition_text || "—"}
					</div>
					<div>
						<span className="text-text-muted">动作：</span>
						{g.action || "—"}
					</div>
					{g.slot_conds?.length ? (
						<div>
							<span className="text-text-muted">信息项条件（只读，按事项已知信息求值）：</span>
							{g.slot_conds.map(slotCondText).join("；")}
						</div>
					) : null}
				</div>
				{g.standing ? (
					<p className="text-sm text-text-secondary">
						常驻指南：不经召回、对所有事项生效，这里不用配召回条件。
					</p>
				) : (
					<RelevanceGuideFields g={g} prof={prof} set={set} />
				)}
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" size="sm" onClick={onClose}>
						取消
					</Button>
					<Button type="button" size="sm" disabled={!!g.standing} onClick={() => onApply(g)}>
						应用到草稿
					</Button>
				</div>
			</div>
		</Drawer>
	);
}

/** 情绪与风险标签显示名：{sentiment ／ risk_flags: {键: 显示名}} */
function signalNamesOf(
	opts?: Record<"sentiment" | "risk_flags", { key: string; name: string }[]>,
): Record<"sentiment" | "risk_flags", Record<string, string>> {
	return {
		sentiment: Object.fromEntries((opts?.sentiment ?? []).map((x) => [x.key, x.name])),
		risk_flags: Object.fromEntries((opts?.risk_flags ?? []).map((x) => [x.key, x.name])),
	};
}

/** 画像 pv5 起的指南召回字段（标签体系 v2 批 4）：适用条件文字（相关性判断按它判）＋ 标签（只加分）＋ 系统事实条件 */
function RelevanceGuideFields({
	g,
	prof,
	set,
}: {
	g: RecallGuide;
	prof: RecallProfile;
	set: (patch: Partial<RecallGuide>) => void;
}) {
	const names = signalNamesOf(prof.signal_tags);
	const sig = g.signal_tags ?? {};
	const setSig = (k: "sentiment" | "risk_flags", v: string[]) => {
		const next = { ...sig, [k]: v };
		const clean = Object.fromEntries(
			Object.entries(next).filter(([, x]) => x?.length),
		) as RecallGuide["signal_tags"];
		set({ signal_tags: clean && Object.keys(clean).length ? clean : undefined });
	};
	return (
		<>
			<Field
				label="适用条件（相关性判断按它逐条判「和这件事对不对得上」，也原样给 N2 ／ N3）"
				hint="写清什么情形下适用；不写挂哪些意图 ／ 工具（对应关系由系统按相关性判断），不照评测题加例子"
			>
				<Textarea
					rows={3}
					value={g.condition_text ?? ""}
					onChange={(e) => set({ condition_text: e.target.value })}
					data-testid="plan-recall-guide-condition"
				/>
			</Field>
			<Field
				label="意图标签（只加分）"
				hint="诉求意图在里面时相关度加分；不在也照样判相关，信号判错意图时这条指南不会消失"
			>
				<ChipPicker
					options={(prof.intents ?? []).map((i) => i.key)}
					value={g.intent_tags ?? []}
					onChange={(v) => set({ intent_tags: v })}
					label={(v) => (prof.intents ?? []).find((i) => i.key === v)?.name ?? v}
					testId="plan-recall-guide-intents"
				/>
			</Field>
			<Field
				label="情绪与风险标签（只加分）"
				hint="本轮信号的情绪 ／ 风险旗命中时加分；只作参考，不硬路由（取值平台内置）"
			>
				<div className="space-y-2">
					<ChipPicker
						options={(prof.signal_tags?.sentiment ?? []).map((x) => x.key)}
						value={sig.sentiment ?? []}
						onChange={(v) => setSig("sentiment", v)}
						label={(v) => `情绪${names.sentiment[v] ?? v}`}
						testId="plan-recall-guide-sentiment"
					/>
					<ChipPicker
						options={(prof.signal_tags?.risk_flags ?? []).map((x) => x.key)}
						value={sig.risk_flags ?? []}
						onChange={(v) => setSig("risk_flags", v)}
						label={(v) => names.risk_flags[v] ?? v}
						testId="plan-recall-guide-risk"
					/>
				</div>
			</Field>
			<Field label="数据条件（系统事实，任一成立；取不到数据时照样召回、待判）">
				<ChipPicker
					options={prof.data_states}
					value={g.data_conds ?? []}
					onChange={(v) => set({ data_conds: v })}
					label={(v) => labelOf(STATE_LABEL, v)}
				/>
			</Field>
		</>
	);
}

function RulesPanel({ prof }: { prof: RecallProfile }) {
	return (
		<div className="space-y-4">
			<RelevanceCard prof={prof} />
			<Card>
				<CardHeader>
					<CardTitle>枚举表（只读；换行业整表替换）</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3 text-sm">
					<EnumRow
						title="业务对象"
						items={prof.entity_types.map((e) => `${labelOf(ENTITY_LABEL, e)}（${e}）`)}
					/>
					{prof.slot_keys?.length ? <EnumRow title="信息项" items={prof.slot_keys} /> : null}
					<EnumRow
						title="数据状态"
						items={prof.data_states.map((s) => `${labelOf(STATE_LABEL, s)}（${s}）`)}
					/>
					<EnumRow
						title="对话状态"
						items={prof.dialogue_states.map((s) => `${labelOf(STATE_LABEL, s)}（${s}）`)}
					/>
				</CardContent>
			</Card>
			<RecallEvalCard />
		</div>
	);
}

/** 画像 pv4 起：工具候选怎么来（相关性为主、意图加分；数字取 `GET /recall/schema` 的 tool_recall，与运行时同一处） */
function RelevanceCard({ prof }: { prof: RecallProfile }) {
	const schema = useRecallSchema();
	const tr = schema.data?.tool_recall;
	const standing =
		prof.tools
			.filter((t) => t.standing)
			.map((t) => t.tool_id)
			.join("、") || "—";
	return (
		<Card data-testid="plan-recall-relevance">
			<CardHeader>
				<CardTitle>工具候选（相关性为主、意图加分）</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2 text-sm text-text-secondary">
				{schema.isError ? (
					<FailState
						reason={`召回结构没读到，下面的数字暂缺：${planErrorMessage(schema.error)}`}
						onRetry={() => schema.refetch()}
						retrying={schema.isFetching}
					/>
				) : null}
				<p>
					① 候选：非常备工具（{tr?.non_standing ?? "—"} 个）全部进相关性判断；多于{" "}
					{tr?.full_judge_max ?? "—"} 个（配置{" "}
					{tr?.full_judge_max_key ?? "plan.recall.tool.full_judge_max"}，值在{" "}
					<PageJump to="config-versions" />
					）才先粗筛，粗筛留下意图标签命中的，其余按原话与说明的字面重合度取前若干个
					{tr?.prefilter ? "（当前已超上限，在粗筛）" : "（当前没超上限，不粗筛）"}。
				</p>
				<p>
					② 相关度：Jev
					按工具说明（做什么、何时用含例句、何时不用）判「描述是否覆盖这件具体的事」，概率即相关度。
				</p>
				<p>
					③ 意图加分：诉求意图在工具「意图标签」里加 {tr?.intent_bonus ?? "—"}（配置{" "}
					{tr?.intent_bonus_key ?? "plan.recall.tool.intent_bonus"}
					）；不在也不剔除，信号判错意图时能力只是少了加分。
				</p>
				<p>
					④ 分档：最终分 ≥ {tr?.yes_at ?? "—"} 推荐、≤ {tr?.no_at ?? "—"}{" "}
					不推荐、其间可选；执行前置确定不满足的标「不推荐」并附原因（不删候选）；推荐工具的前置不低于可选。每个候选留痕相关度、意图加分、最终分和为什么在这档。
				</p>
				<p>
					⑤ 常备（{standing}）每件事项都可用；N2 看得到全部候选，推荐 ／
					可选档给全量说明、不推荐档一句话，粗筛掉的以「编号＋一句话」挂在最后（候选外目录）。说明写不清导致没召回，就改说明，不加对应关系。
				</p>
			</CardContent>
		</Card>
	);
}

function EnumRow({ title, items }: { title: string; items: string[] }) {
	return (
		<div className="flex flex-wrap items-start gap-2">
			<span className="w-20 shrink-0 text-xs text-text-muted">{title}</span>
			<span className="flex flex-wrap gap-1.5">
				{items.map((x) => (
					<Badge key={x} tone="muted">
						{x}
					</Badge>
				))}
			</span>
		</div>
	);
}

function SaveDialog({
	base,
	draft,
	nChanged,
	onClose,
	onSaved,
}: {
	base: string;
	draft: Draft;
	nChanged: number;
	onClose: () => void;
	onSaved: (version: string) => void;
}) {
	const reg = useRecallRegression();
	const save = useRecallSaveProfile();
	const [note, setNote] = useState("");
	// biome-ignore lint/correctness/useExhaustiveDependencies: 打开时跑一次回归
	useEffect(() => {
		reg.mutate({ base_version: base, tools: draft.tools, guides: draft.guides });
	}, []);
	const passed = reg.data?.passed === true;
	const reason = reg.isPending
		? "回归还在跑"
		: !passed
			? reg.data?.jev_failed
				? `回归判不出：Jev 有 ${reg.data.jev_failed} 次调用失败，稍后重试`
				: "回归不过：召回率降了或误召回升了"
			: !note.trim()
				? "备注必填"
				: undefined;
	return (
		<Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
			<DialogContent data-testid="plan-recall-save-dialog">
				<DialogHeader>
					<DialogTitle>保存为新版本</DialogTitle>
					<DialogDescription>
						底版本 {base} · 草稿改了 {nChanged} 处 · 保存后立即生效
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-3 text-sm">
					<div className="space-y-1.5">
						<div className="text-xs text-text-secondary">
							回归（开发集，Jev 臂：粗召回 → Jev 是非题 →
							分档，与运行时同一条路；底版本和草稿各跑一遍，草稿只问改到的题）
						</div>
						{reg.isPending ? (
							<div data-testid="plan-recall-regression-wait">
								<LoadingInline text="回归中…约 50～100 次 Jev 调用，要等十几秒到半分钟，请别关窗口" />
							</div>
						) : null}
						{reg.isError ? (
							<FailState
								reason={planErrorMessage(reg.error)}
								onRetry={() => reg.mutate({ base_version: base, ...draft })}
								retrying={reg.isPending}
							/>
						) : null}
						{reg.data
							? reg.data.rows.map((r) => (
									<div
										key={r.metric}
										className="flex items-center gap-2"
										data-testid="plan-recall-regression-row"
									>
										<span className="w-24 text-text-secondary">
											{METRIC_LABEL[r.metric] ?? r.metric}
										</span>
										<span className="font-mono">
											{fmtRate(r.before)} → {fmtRate(r.after)}
										</span>
										<Badge tone={r.ok ? "success" : "danger"}>{r.ok ? "通过" : "变差"}</Badge>
									</div>
								))
							: null}
						{reg.data?.jev_calls != null ? (
							<div className="text-xs text-text-muted" data-testid="plan-recall-regression-meta">
								{reg.data.jev_model} · {reg.data.decision_points} 个决策点 · Jev 调用{" "}
								{reg.data.jev_calls} 次
								{reg.data.jev_failed ? `（失败 ${reg.data.jev_failed} 次）` : ""}
								{reg.data.seconds != null ? ` · 用时 ${reg.data.seconds} 秒` : ""}
							</div>
						) : null}
					</div>
					<div className="space-y-1.5">
						<div className="text-xs text-text-secondary">备注（必填）</div>
						<Textarea
							rows={2}
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="这次改了什么、为什么"
						/>
					</div>
					{save.isError ? (
						<p className="text-xs text-brand-danger">{planErrorMessage(save.error)}</p>
					) : null}
				</div>
				<DialogFooter>
					<Button type="button" variant="outline" size="sm" onClick={onClose}>
						取消
					</Button>
					<Button
						type="button"
						size="sm"
						disabled={!!reason || save.isPending}
						title={reason}
						onClick={() =>
							save.mutate(
								{ base_version: base, tools: draft.tools, guides: draft.guides, note },
								{ onSuccess: (v) => onSaved(v.version) },
							)
						}
					>
						{save.isPending ? "保存中…" : "保存"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
